import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "./DraggableIcons.css"; // Import your CSS file for styles

function DraggableIcons() {
  const initialIconPositions = {
    shape1: { x: -480, y: -220 }, // Top Left -> 🎬
    shape2: { x: 480, y: -200 },  // Top Right -> 🎉
    shape3: { x: -450, y: 250 },  // Bottom Left -> 📽️
    shape4: { x: 450, y: 280 },   // Bottom Right -> 🤝
  };

  const [iconPositions, setIconPositions] = useState(initialIconPositions);
  const [initialPositions, setInitialPositions] = useState({});
  const [draggingIcon, setDraggingIcon] = useState(null);

  useEffect(() => {
    const handleMouseMove = (event) => {
      if (!draggingIcon || !initialPositions[draggingIcon]) return;

      const { clientX, clientY } = event;
      const deltaX = clientX - initialPositions[draggingIcon].x;
      const deltaY = clientY - initialPositions[draggingIcon].y;

      setIconPositions((prevPositions) => ({
        ...prevPositions,
        [draggingIcon]: {
          x: prevPositions[draggingIcon].x + deltaX,
          y: prevPositions[draggingIcon].y + deltaY,
        },
      }));

      setInitialPositions((prevPositions) => ({
        ...prevPositions,
        [draggingIcon]: { x: clientX, y: clientY },
      }));
    };

    const handleMouseUp = () => {
      if (draggingIcon) {
        setDraggingIcon(null);
        setInitialPositions({});
        // Reset to original position on release
        setIconPositions(initialIconPositions);
      }
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    if (draggingIcon) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [draggingIcon, initialPositions]);

  const handleMouseDown = (event, icon) => {
    event.preventDefault(); // Prevent text selection
    const { clientX, clientY } = event;
    setInitialPositions((prevPositions) => ({
      ...prevPositions,
      [icon]: { x: clientX, y: clientY },
    }));
    setDraggingIcon(icon);
  };

  return (
    <div className="relative min-h-[95vh] flex flex-col items-center justify-center pt-32 overflow-visible text-center px-4">
      {/* Hero Content */}
      <div className="z-20 max-w-5xl animate-in fade-in slide-in-from-bottom-8 duration-700">
        <h1 className="hero-title text-3xl md:text-4xl lg:text-5xl font-black text-black mb-6 leading-[1.2] tracking-tight">
          Empower Your Viral <br className="hidden lg:block" />
          Growth with <br className="hidden lg:block" />
          <span className="highlight-pill">#Collaborative</span> <br className="hidden lg:block" />
          Campaigns
        </h1>
        
        <p className="hero-subtitle mb-12 text-sm md:text-base lg:text-lg text-gray-600 max-w-3xl mx-auto font-medium px-4 opacity-80">
          Experience seamless connections and creativity with Braccet. <br className="hidden md:block" />
          Our platform unites brands and creators to produce viral content 
          that captivates and engages audiences.
        </p>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-6 px-4">
          <Link 
            to="/Creator"
            className="w-full sm:w-auto px-10 py-5 bg-black text-white rounded-full text-xl font-bold shadow-2xl transition-all hover:scale-105 active:scale-95 min-w-[240px]"
          >
            For Creators
          </Link>
          <Link 
            to="/Brand"
            className="w-full sm:w-auto px-10 py-5 bg-white text-black border-2 border-black rounded-full text-xl font-bold shadow-xl transition-all hover:scale-105 active:scale-95 min-w-[240px]"
          >
            For Brands
          </Link>
        </div>
      </div>

      {/* Floating Decorative Shapes (Draggable) */}
      <div className="icons-container">
        {/* Red Circle Shape */}
        <div
          className="icon sticker-shape-red shadow-lg"
          style={{
            transform: `translate(calc(-50% + ${iconPositions.shape1.x}px), calc(-50% + ${iconPositions.shape1.y}px)) rotate(-15deg)`,
          }}
          onMouseDown={(e) => handleMouseDown(e, "shape1")}
        >
          🎬
        </div>

        {/* Blue Rounded Box Shape */}
        <div
          className="icon sticker-shape-blue shadow-lg"
          style={{
            transform: `translate(calc(-50% + ${iconPositions.shape2.x}px), calc(-50% + ${iconPositions.shape2.y}px)) rotate(10deg)`,
          }}
          onMouseDown={(e) => handleMouseDown(e, "shape2")}
        >
          🎉
        </div>

        {/* Yellow Box Shape (Consistency Fix) */}
        <div
          className="icon sticker-shape-yellow shadow-lg"
          style={{
            transform: `translate(calc(-50% + ${iconPositions.shape3.x}px), calc(-50% + ${iconPositions.shape3.y}px)) rotate(15deg)`,
          }}
          onMouseDown={(e) => handleMouseDown(e, "shape3")}
        >
          📽️
        </div>

        {/* Purple Rounded Box Shape */}
        <div
          className="icon sticker-shape-purple shadow-lg"
          style={{
            transform: `translate(calc(-50% + ${iconPositions.shape4.x}px), calc(-50% + ${iconPositions.shape4.y}px)) rotate(-5deg)`,
          }}
          onMouseDown={(e) => handleMouseDown(e, "shape4")}
        >
          🤝
        </div>
      </div>
    </div>
  );
}

export default DraggableIcons;
